import { useState, useEffect, useRef } from 'react'
import cutileiApi, { UPLOAD_TIMEOUT } from 'services/cutileiApi'
import BankingService from 'services/Banking'
import useRefreshOnReturn from 'hooks/useRefreshOnReturn'
import FileInput from 'components/Inputs/FileInput'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { FaCheck, FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa'

import {
  Container,
  Content,
  Card,
  CardHeader,
  CardTitle,
  Caption,
  HeroCard,
  HeroLabel,
  HeroDetail,
  HeroFooter,
  DocumentCard,
  DocumentInfo,
  DocumentMeta,
  StatusGrid,
  StatusCell,
  StatusBadge,
  Dot,
  StepList,
  Step,
  EmptyState,
  Divider,
  WarningText,
  ExternalLinkButton
} from '../../styles'

const ACCEPTED_FILES = '.jpg,.jpeg,.png,.webp,.pdf'

/*
  Quando o titular volta da página do Asaas, a situação é relida agora e de novo nestes
  segundos: o Asaas processa o envio depois que a página fecha.
*/
const RETURN_FOLLOW_UPS_SECONDS = [5, 15, 30, 60]

export default function Onboarding ({
  account,
  requestConfig,
  onAccountUpdated: handleAccountUpdated = () => {}
}) {
  const [loading, setLoading] = useState (true)
  const [syncing, setSyncing] = useState (false)
  const [uploadingId, setUploadingId] = useState (null)
  const [onboarding, setOnboarding] = useState (null)
  const [pending, setPending] = useState (false)
  const [message, setMessage] = useState (null)
  const [requestError, setRequestError] = useState (null)
  const retryTimeout = useRef ()
  const followUps = useRef ([])
  const fetching = useRef (false)
  const awaitingReturn = useRef (false)

  useEffect (() => {
    getData ()

    return () => {
      clearTimeout (retryTimeout.current)
      clearFollowUps ()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
    O envio de documentos acontece numa página do Asaas, aberta em outra aba, e a situação
    do cadastro muda lá. Voltar para cá com a leitura da montagem mostraria o cadastro de
    antes do envio.
  */
  useRefreshOnReturn (() => {
    if (!awaitingReturn.current) return getData ()

    awaitingReturn.current = false
    refreshAfterReturn ()
  })

  /*
    A volta de qualquer aba relê o cadastro uma vez. A volta da página do Asaas — marcada no
    clique que a abre — relê de novo no primeiro minuto: o Asaas processa o que o titular
    enviou lá depois que a página fecha, e uma leitura só, na hora da volta, ainda encontrava
    o cadastro de antes do envio.
  */
  const refreshAfterReturn = () => {
    clearFollowUps ()
    getData ()

    followUps.current = RETURN_FOLLOW_UPS_SECONDS.map (
      seconds => setTimeout (getData, seconds * 1000)
    )
  }

  const clearFollowUps = () => {
    followUps.current.forEach (clearTimeout)
    followUps.current = []
  }

  /*
    A tela do Cutilei Pay decide pelo `canTransact` o que mostrar, e relê a conta do banco
    local, que só esta leitura atualiza a partir do Asaas. Cada leitura daqui repassa a
    situação para lá, e é isso que troca o acompanhamento pelas abas quando a conta é
    aprovada. A forma de função é porque quem chama pode ser um temporizador armado com a
    conta de antes.
  */
  const publishAccount = data => handleAccountUpdated (prev => (
    !prev || (prev.status === data.status && prev.canTransact === data.canTransact)
      ? prev
      : {...prev, status: data.status, canTransact: data.canTransact}
  ))

  /*
    O Asaas exige 15 segundos entre criar a subconta e consultar seus documentos. A API
    devolve `pending` com o tempo restante em vez de chamar o Asaas cedo demais, então
    aqui só reagendamos a consulta.

    Uma consulta por vez: a volta à aba e a espera dos 15 segundos chegam juntas, e cada
    uma que passasse armaria a sua própria corrente.
  */
  const getData = async () => {
    if (fetching.current) return

    clearTimeout (retryTimeout.current)
    fetching.current = true

    try {
      setLoading (true)
      const { data } = await cutileiApi.get (
        `/banking_account/onboarding${BankingService.getOwnerQuery ()}`, requestConfig
      )

      setOnboarding (data)
      setPending (data.pending ?? false)
      setMessage (data.message ?? null)
      setRequestError (null)
      publishAccount (data)

      if (data.pending) {
        retryTimeout.current = setTimeout (getData, (data.retryAfter ?? 5) * 1000)
      }
    } catch (error) {
      console.log (error.response ?? error)
      setRequestError (
        error.response?.data?.message ?? 'Não foi possível carregar o seu cadastro'
      )
    } finally {
      fetching.current = false
      setLoading (false)
    }
  }

  const syncStatus = async () => {
    try {
      setSyncing (true)
      setRequestError (null)

      const { data } = await cutileiApi.post (
        `/banking_account/sync${BankingService.getOwnerQuery ()}`, {}, requestConfig
      )

      /*
        `/sync` só atualiza a situação cadastral: ele não consulta os documentos no Asaas,
        que é outra chamada, e por isso devolve `pendingDocuments: null`. Sem preservar a
        lista, um clique em "Atualizar" fazia o cartão de documentos sumir da tela.
      */
      setOnboarding (prev => ({
        ...prev,
        ...data,
        pendingDocuments: data.pendingDocuments ?? prev?.pendingDocuments
      }))

      publishAccount (data)
    } catch (error) {
      console.log (error.response ?? error)
      setRequestError (
        error.response?.data?.message ?? 'Não foi possível atualizar sua situação'
      )
    } finally {
      setSyncing (false)
    }
  }

  const uploadDocument = async (document, file) => {
    if (!file) return

    try {
      setUploadingId (document.id)
      setRequestError (null)

      const formData = new FormData ()
      formData.append ('documentFile', file, file.name)
      formData.append ('type', document.type)

      await cutileiApi.post (`/banking_account/documents/${document.id}`, formData, {
        // Upload/download nao cabe no timeout padrao: rede movel ruim leva mais que 30 s.
        timeout: UPLOAD_TIMEOUT,
        headers: {
          ...requestConfig.headers,
          'Content-Type': 'multipart/form-data'
        }
      })

      await getData ()
    } catch (error) {
      console.log (error.response ?? error)

      /*
        O Asaas recusa por API os documentos do fluxo hospedado dele. Quando isso acontece,
        a API já renovou o link e o devolve aqui — recarregar traz o documento no modo
        certo, com o botão do banco no lugar do que acabou de falhar.
      */
      if (error.response?.data?.code === 'document_requires_onboarding') await getData ()

      setRequestError (
        error.response?.data?.message ?? 'Não foi possível enviar o documento'
      )
    } finally {
      setUploadingId (null)
    }
  }

  if (loading && !onboarding) return <Loading style={{marginTop: 40}}/>

  const status = onboarding?.status ?? account.status
  const asaasStatus = onboarding?.asaasStatus ?? {}
  const accountNumber = onboarding?.accountNumber
  const documents = onboarding?.pendingDocuments ?? []
  const currentStep = BankingService.getCurrentStep (status, asaasStatus)
  const progress = BankingService.getDocumentProgress (documents)

  /*
    O link da conta serve de reserva para o documento que não tem o seu. É o mesmo link,
    gerado pelo Asaas para o cadastro inteiro.
  */
  const accountOnboardingUrl = onboarding?.onboardingUrl ?? null
  const canSendDocuments = BankingService.canSendDocuments ()
  const rejectReasons = onboarding?.rejectReasons ?? null

  /*
    Formatos e tamanho vêm da API para não divergirem do que ela aceita — só o `accept` do
    input tem um padrão local, porque a resposta pode ainda não ter chegado.
  */
  const constraints = onboarding?.uploadConstraints
  const uploadHint = BankingService.getUploadHint (constraints)
  const acceptedFiles = constraints?.acceptedExtensions
    ? constraints.acceptedExtensions.map (extension => `.${extension}`).join (',')
    : ACCEPTED_FILES

  /*
    Com a conta aprovada o cartão do andamento some, mas atualizar a situação continua
    valendo — o botão acompanha o cartão que sobra em vez de sumir junto.
  */
  const isActive = status === 'active'
  const refreshButton = (
    <SecondaryButton
      label='Atualizar'
      leftIcon={<FaSyncAlt size={12} color='#FFFFFF'/>}
      loading={syncing}
      onClick={syncStatus}
      width={130}
      height={34}
    />
  )

  return (
    <Container>
      <Content>
        <HeroCard>
          <HeroLabel>Minha conta Cutilei</HeroLabel>
          <StatusBadge
            color={BankingService.getStatusColor (status)}
            style={{marginTop: 8, backgroundColor: '#FFFFFF14'}}
          >
            <Dot/>
            {BankingService.getStatusLabel (status)}
          </StatusBadge>
          {accountNumber && (
            <HeroFooter>
              <HeroDetail>
                <span>Agência e conta</span>
                <b>
                  {`${accountNumber.agency} / ${accountNumber.account}-${accountNumber.digit}`}
                </b>
              </HeroDetail>
            </HeroFooter>
          )}
        </HeroCard>
        {pending ? (
          <Card>
            <EmptyState>
              <Loading/>
              <b>Estamos preparando sua conta</b>
              <p>A lista de documentos aparecerá aqui em instantes</p>
            </EmptyState>
          </Card>
        ) : documents.length > 0 && (
          <Card gap={12}>
            <CardHeader>
              <CardTitle>Documentos</CardTitle>
              <StatusBadge color={progress.pending > 0 ? '#FF9800' : '#44CF6C'}>
                <Dot/>
                {`${progress.sent} de ${progress.total} enviados`}
              </StatusBadge>
            </CardHeader>
            <Caption>
              {progress.pending > 0
                ? 'Envie os documentos abaixo para concluir a abertura da sua conta'
                : 'Tudo enviado. O banco avisa assim que terminar de conferir'}
            </Caption>
            <Divider/>
            {documents.map (document => {
              const sentCount = document.sentCount ?? 0
              const rejected = document.status === 'REJECTED'

              /*
                Quem decide o caminho é a API, não o `onboardingUrl` sozinho: há grupo que
                vem sem link e mesmo assim recusa o envio. Sem link e sem API, o titular
                precisa ler o que fazer em vez de encontrar um botão que sempre falha.
              */
              const documentUrl = document.onboardingUrl ?? accountOnboardingUrl
              const canUpload = document.canUploadViaApi ?? !document.onboardingUrl

              return (
                <DocumentCard key={document.id} highlight={rejected}>
                  <DocumentInfo>
                    <b>{document.title}</b>
                    <p>{document.description}</p>
                    <StatusBadge color={BankingService.getStatusColor (document.status)}>
                      <Dot/>
                      {BankingService.getAsaasStatusLabel (document.status)}
                    </StatusBadge>
                    {rejected && (
                      <WarningText style={{margin: 0}}>
                        {'O banco recusou o que foi enviado. Envie outro arquivo para '
                        + 'seguir com a abertura'}
                      </WarningText>
                    )}
                    {sentCount > 0 && (
                      <DocumentMeta>
                        {sentCount === 1
                          ? '1 arquivo enviado'
                          : `${sentCount} arquivos enviados`}
                      </DocumentMeta>
                    )}
                    <DocumentMeta>
                      {canUpload
                        ? canSendDocuments ? uploadHint : 'Só o titular da conta envia este documento'
                        : documentUrl
                          ? 'Por exigência do Banco Central, este documento é enviado no '
                            + 'site do banco parceiro'
                          : 'Este documento é enviado no site do banco parceiro, e o link '
                            + 'ainda não está disponível. Toque em Atualizar em alguns '
                            + 'minutos para tentar de novo'}
                    </DocumentMeta>
                  </DocumentInfo>
                  {!canUpload ? (
                    /*
                      Identificação com reconhecimento facial não passa pela API por
                      determinação regulatória — só pela interface hospedada do Asaas.
                    */
                    documentUrl && (
                      <ExternalLinkButton
                        href={documentUrl}
                        onClick={() => { awaitingReturn.current = true }}
                      >
                        Enviar no site do banco
                        <FaExternalLinkAlt size={12} color='#FFC100'/>
                      </ExternalLinkButton>
                    )
                  ) : canSendDocuments && (
                    uploadingId === document.id ? (
                      <ButtonLoading/>
                    ) : (
                      <FileInput
                        label={sentCount > 0 ? 'Enviar outro' : 'Enviar arquivo'}
                        accept={acceptedFiles}
                        onFileSelected={file => uploadDocument (document, file)}
                      />
                    )
                  )}
                </DocumentCard>
              )
            })}
          </Card>
        )}
        {/*
          O andamento só existe enquanto há abertura em andamento: aprovada a conta, todos
          os passos ficam marcados e o cartão vira enfeite na frente do que o titular vem
          ver aqui depois — o número da conta e a situação no banco.
        */}
        {!isActive && (
          <Card gap={12}>
            <CardHeader>
              <CardTitle>Andamento da abertura</CardTitle>
              {refreshButton}
            </CardHeader>
            {message && <Caption>{message}</Caption>}
            <StepList>
              {BankingService.ONBOARDING_STEPS.map ((step, index) => (
                <Step key={step.label} done={index < currentStep} current={index === currentStep}>
                  <div className='marker'>
                    {index < currentStep ? <FaCheck size={12} color='#FFFFFF'/> : index + 1}
                  </div>
                  <div className='text'>
                    <b>{step.label}</b>
                    <p>{step.description}</p>
                  </div>
                </Step>
              ))}
            </StepList>
            {rejectReasons && (
              <WarningText>{`Motivo da recusa: ${rejectReasons}`}</WarningText>
            )}
          </Card>
        )}
        <Card gap={12}>
          <CardHeader>
            <CardTitle>Situação no banco</CardTitle>
            {isActive && refreshButton}
          </CardHeader>
          <StatusGrid>
            {Object.keys (BankingService.ASAAS_STATUS_TITLES).map (key => (
              <StatusCell key={key}>
                <span>{BankingService.ASAAS_STATUS_TITLES[key]}</span>
                <StatusBadge color={BankingService.getStatusColor (asaasStatus[key])}>
                  <Dot/>
                  {BankingService.getAsaasStatusLabel (asaasStatus[key])}
                </StatusBadge>
              </StatusCell>
            ))}
          </StatusGrid>
          {requestError && <WarningText>{requestError}</WarningText>}
        </Card>
      </Content>
    </Container>
  )
}
